import { DataGrid } from "@mui/x-data-grid";
import { Box, Card } from "@mui/material";

import { getLocaleText } from "./localeText";
import { DataGridToolbar } from "./DataGridToolbar";
import { resolveRowId, normalizeRows } from "./rowId";

import type { ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import type { DataGridProps, GridColDef, GridCsvExportOptions } from "@mui/x-data-grid";
import type { CallerRowIdResolver, GridRowLike } from "./rowId";
import type { DataGridToolbarLabels } from "./DataGridToolbar";

/**
 * El resto de props viaja tal cual al DataGrid, por eso extiende DataGridProps. Se omiten
 * las que este wrapper controla o cuya forma relaja (filas sin id obligatorio, columnas a
 * las que les inyecta minWidth).
 */
export interface DataGridTableProps
  extends Omit<
    DataGridProps,
    // `pagination` colisiona por nombre pero no por significado: en DataGrid es
    // `true | undefined` (la community siempre pagina), y acá controla si se muestra el
    // footer. Se desestructura antes del spread, así que el DataGrid nunca lo recibe.
    "rows" | "columns" | "sx" | "getRowId" | "localeText" | "pagination"
  > {
  rows?: GridRowLike[];
  columns?: GridColDef[];
  title?: string;
  loading?: boolean;
  language?: string;
  getRowId?: CallerRowIdResolver;
  height?: number | string;
  pagination?: boolean;
  pageSize?: number;
  search?: boolean;
  download?: boolean;
  filter?: boolean;
  showColumns?: boolean;
  toolbarActions?: ReactNode;
  csvOptions?: GridCsvExportOptions;
  toolbarLabels?: DataGridToolbarLabels;
  toolbar?: boolean;
  sx?: SxProps<Theme>;
}

const DEFAULT_PAGE_SIZE = 10;
const BASE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const COLUMN_MIN_WIDTH = 130;

const HEADER_COLOR = "#1976D2";

const MD2_GRID_SX = {
  border: "none",
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: 14,
    fontWeight: 500,
    color: HEADER_COLOR,
  },
  "& .MuiDataGrid-cell": { fontSize: 14 },
  "& .MuiTablePagination-spacer": { display: "none" },
  "& .MuiTablePagination-toolbar": { justifyContent: "center" },
};

/**
 * Idiomatic MUI X DataGrid wrapper shared across the portal.
 *
 * Replaces the mui-datatables-based MuiTable: columns follow the native
 * DataGrid shape (`field` / `renderCell` with `params.row`), not the positional
 * `customBodyRender(value, tableMeta)` model. The wrapper only centralizes the
 * cross-cutting concerns every table repeated by hand: i18n localeText, the
 * toolbar (search / CSV export / columns / filters), pagination defaults, a
 * bounded height, and the row-id fallback DataGrid requires.
 *
 * @param {object[]} columns     Native DataGrid column definitions.
 * @param {boolean}  [loading]   Shows the grid loading overlay.
 * @param {Function} [getRowId]  Custom row id resolver; defaults to row.id with
 *                               an array-index fallback when the payload has none.
 * @param {boolean}  [pagination]    Show the pagination footer (default true).
 * @param {boolean}  [search]        Quick-filter search box (default true).
 * @param {boolean}  [filter]        Filter panel button (default true).
 * @param {React.ReactNode} [toolbarActions] Extra toolbar buttons (reload, ...).
 * @param {object}   [toolbarLabels] i18n labels for the toolbar buttons.
 * @param {object}   [sx]            Extra sx merged over the parity defaults.
 */
export const DataGridTable = ({
  rows = [],
  columns = [],
  title,
  loading = false,
  language = "es",
  getRowId,
  height = 420,
  pagination = true,
  pageSize = DEFAULT_PAGE_SIZE,
  search = true,
  download = true,
  filter = true,
  showColumns = true,
  toolbarActions = null,
  csvOptions,
  toolbarLabels,
  toolbar = true,
  sx,
  ...rest
}: DataGridTableProps) => {
  const normalizedRows = normalizeRows(rows, getRowId);
  const getRowIdFn = resolveRowId(getRowId);

  const gridColumns = columns.map((column) => ({ minWidth: COLUMN_MIN_WIDTH, ...column }));

  const pageSizeOptions = Array.from(
    new Set([pageSize, ...BASE_PAGE_SIZE_OPTIONS])
  ).sort((a, b) => a - b);

  const toolbarProps = toolbar
    ? {
        showToolbar: true,
        slots: { toolbar: DataGridToolbar },
        slotProps: {
          toolbar: {
            title,
            search,
            download,
            filter,
            showColumns,
            actions: toolbarActions,
            csvOptions,
            labels: toolbarLabels,
          },
        },
      }
    : {};

  return (
    <Card sx={{ p: 2 }}>
      <Box sx={{ height, width: "100%" }}>
        <DataGrid
          rows={normalizedRows}
          columns={gridColumns}
          loading={loading}
          getRowId={getRowIdFn}
          localeText={getLocaleText(language)}
          initialState={{ pagination: { paginationModel: { pageSize } } }}
          pageSizeOptions={pageSizeOptions}
          hideFooter={!pagination}
          disableRowSelectionOnClick
          sx={{ ...MD2_GRID_SX, ...sx }}
          {...toolbarProps}
          {...rest}
        />
      </Box>
    </Card>
  );
};
